import { supabase } from '../lib/supabase';
import { searchPlaceWithDetails } from './googleMapsPlaceService';
import { stateParkService } from './stateParkService';
import { tripSummaryService } from './tripSummaryService';

// ✅ COMPLETELY FIXED: Utility function to get upcoming Friday-Sunday dates based on current date
const getUpcomingWeekendDates = () => {
  const today = new Date();
  today?.setHours(0, 0, 0, 0); // Reset to start of day for accurate comparison
  
  const currentDay = today?.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  
  // Calculate days until next Friday (day 5)
  let daysUntilFriday;
  
  if (currentDay === 5) {
    // If it's Friday, get next Friday (7 days away)
    daysUntilFriday = 7;
  } else if (currentDay === 6) {
    // If it's Saturday, get next Friday (6 days away)
    daysUntilFriday = 6;
  } else if (currentDay === 0) {
    // If it's Sunday, get next Friday (5 days away)
    daysUntilFriday = 5;
  } else {
    // Monday through Thursday: Get this coming Friday
    daysUntilFriday = 5 - currentDay;
  }
  
  // Calculate Friday date
  const nextFriday = new Date(today);
  nextFriday?.setDate(today?.getDate() + daysUntilFriday);
  
  // Calculate Sunday date (2 days after Friday)
  const followingSunday = new Date(nextFriday);
  followingSunday?.setDate(nextFriday?.getDate() + 2);
  
  // Format as YYYY-MM-DD for PostgreSQL date format
  const formatDate = (date) => {
    const year = date?.getFullYear();
    const month = String(date?.getMonth() + 1)?.padStart(2, '0');
    const day = String(date?.getDate())?.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const result = {
    startDate: formatDate(nextFriday),
    endDate: formatDate(followingSunday)
  };
  
  console.log('📅 Calculated upcoming weekend:', {
    today: formatDate(today),
    currentDay: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']?.[currentDay],
    daysUntilFriday,
    startDate: result?.startDate,
    endDate: result?.endDate
  });
  
  return result;
};

/**
 * Enhanced caching system for state park location data
 * Creates a dedicated cache for park location data to minimize API calls
 */
const createStateParkLocationCache = () => {
  try {
    // Initialize location cache for state parks
    const locationCache = JSON.parse(localStorage.getItem('stateParkLocationCache') || '{}');
    return locationCache;
  } catch (error) {
    console.warn('⚠️ Could not load location cache, initializing empty cache');
    return {};
  }
};

/**
 * Save location data to cache
 */
const saveLocationToCache = (parkName, locationData) => {
  try {
    const cache = createStateParkLocationCache();
    cache[parkName] = {
      ...locationData,
      cachedAt: new Date()?.toISOString(),
      // Cache expires after 30 days
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString()
    };
    localStorage.setItem('stateParkLocationCache', JSON.stringify(cache));
    console.log('💾 Cached location data for:', parkName);
  } catch (error) {
    console.warn('⚠️ Could not save location to cache:', error);
  }
};

/**
 * Get cached location data if valid
 */
const getCachedLocationData = (parkName) => {
  try {
    const cache = createStateParkLocationCache();
    const cachedData = cache?.[parkName];
    
    if (!cachedData) return null;
    
    // Check if cache has expired
    const now = new Date();
    const expiresAt = new Date(cachedData.expiresAt);
    
    if (now > expiresAt) {
      console.log('🕐 Cache expired for:', parkName);
      return null;
    }
    
    console.log('✅ Using cached location data for:', parkName);
    return {
      state: cachedData?.state,
      city: cachedData?.city,
      placeId: cachedData?.placeId
    };
  } catch (error) {
    console.warn('⚠️ Error reading cache:', error);
    return null;
  }
};

/**
 * Enhance trip data with automatic park information
 * Fetches park details from state_parks table and Google Places API
 * @param {Object} tripData - Trip data to enhance
 * @returns {Promise<Object>} Enhanced trip data
 */
const enhanceTripWithParkData = async (tripData) => {
  try {
    console.log('🔄 Enhancing trip with park data:', tripData);

    // If park name is provided, try to fetch data
    if (tripData?.park) {
      // First check cache for location data
      const cachedLocation = getCachedLocationData(tripData?.park);
      
      if (cachedLocation) {
        // Use cached data
        if (!tripData?.state && cachedLocation?.state) {
          tripData.state = cachedLocation?.state;
        }
        if (!tripData?.city && cachedLocation?.city) {
          tripData.city = cachedLocation?.city;
        }
        if (!tripData?.placeId && cachedLocation?.placeId) {
          tripData.placeId = cachedLocation?.placeId;
        }
        console.log('✅ Used cached location data for:', tripData?.park);
      } else {
        // Try to get park data from state_parks database
        const parkData = await stateParkService?.getParkByName(tripData?.park);
        
        if (parkData) {
          console.log('✅ Found park in database:', parkData);
          
          // ✅ Store state separately
          if (parkData?.state && !tripData?.state) {
            tripData.state = parkData?.state;
          }
          
          // Update trip data with park information if not already provided
          if (!tripData?.destination && parkData?.state) {
            tripData.destination = `${parkData?.name}, ${parkData?.state}`;
          }
          
          // Store website URL in notes if available and notes are empty
          if (parkData?.websiteUrl && !tripData?.notes) {
            tripData.notes = `Park Website: ${parkData?.websiteUrl}\n\n${parkData?.description || ''}`;
          }
        }

        // Try to get place_id, state, and city from Google Places if not already provided
        if (!tripData?.placeId || !tripData?.state || !tripData?.city) {
          try {
            const placeDetails = await searchPlaceWithDetails(
              tripData?.park,
              null // No location bias
            );
            
            if (placeDetails) {
              console.log('✅ Found place details from Google Places:', placeDetails);
              
              // Store place_id
              if (!tripData?.placeId && placeDetails?.placeId) {
                tripData.placeId = placeDetails?.placeId;
              }
              
              // ✅ Store state separately
              if (placeDetails?.state && !tripData?.state) {
                tripData.state = placeDetails?.state;
              }
              
              // ✅ NEW: Store city separately
              if (placeDetails?.city && !tripData?.city) {
                tripData.city = placeDetails?.city;
              }
              
              // Cache the location data for future use
              saveLocationToCache(tripData?.park, {
                state: placeDetails?.state,
                city: placeDetails?.city,
                placeId: placeDetails?.placeId
              });
              
              // ✅ UPDATED: Build destination with city and state if available
              if (!tripData?.destination) {
                if (placeDetails?.city && placeDetails?.state) {
                  tripData.destination = `${tripData?.park}, ${placeDetails?.city}, ${placeDetails?.state}`;
                } else if (placeDetails?.state) {
                  tripData.destination = `${tripData?.park}, ${placeDetails?.state}`;
                }
              }
            }
          } catch (placeError) {
            console.warn('⚠️ Could not fetch Google Places data:', placeError);
            // Continue without Google Places data
          }
        }
      }
    }

    console.log('🎯 Enhanced trip data with location caching:', tripData);
    return tripData;
  } catch (error) {
    console.error('❌ Error enhancing trip with park data:', error);
    // Return original data if enhancement fails
    return tripData;
  }
};

export const tripService = {
  // ✅ OPTIMIZED: Get all trips for a specific user with performance improvements
  async getUserTrips(userId) {
    const startTime = performance.now();
    
    try {
      const { data, error } = await supabase
        ?.from('trips')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch trips: ${error.message}`);
      }

      const mappedData = data?.map(trip => ({
        id: trip?.id,
        title: trip?.title,
        destination: trip?.destination,
        park: trip?.park,
        state: trip?.state,
        city: trip?.city,
        placeId: trip?.place_id,
        description: trip?.description,
        startDate: trip?.start_date,
        endDate: trip?.end_date,
        duration: trip?.duration,
        travelers: trip?.travelers,
        totalCost: trip?.total_cost,
        accommodation: trip?.accommodation,
        activities: trip?.activities || [],
        notes: trip?.notes,
        status: trip?.status || 'draft',
        isPublic: trip?.is_public || false,
        imageUrl: trip?.image_url,
        imageAlt: trip?.image_alt,
        aiTripData: trip?.ai_trip_data,
        timeframeText: trip?.timeframe_text,
        createdAt: trip?.created_at,
        updatedAt: trip?.updated_at
      })) || [];

      // ✅ NEW: Log performance metrics
      const endTime = performance.now();
      const executionTime = Math.round(endTime - startTime);
      
      if (executionTime > 1000) {
        console.warn('⚠️ Slow query detected:', {
          operation: 'getUserTrips',
          executionTime: `${executionTime}ms`,
          recordCount: mappedData?.length
        });
      }

      return mappedData;
    } catch (error) {
      console.error('❌ Error in getUserTrips:', error);
      throw error;
    }
  },

  // ✅ NEW: Get effective visibility for a trip
  async getEffectiveVisibility(tripId, userId) {
    try {
      const { data, error } = await supabase
        ?.rpc('get_effective_trip_visibility', {
          p_trip_id: tripId,
          p_user_id: userId
        });

      if (error) throw error;
      return data || 'private'; // Default to private if no data
    } catch (error) {
      console.error('Error getting effective visibility:', error);
      return 'private'; // Fail-safe to private
    }
  },

  // ✅ NEW: Update trip visibility override
  async updateTripVisibilityOverride(tripId, visibilityOverride) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('trips')
        ?.update({ 
          visibility_override: visibilityOverride, // Can be 'public', 'private', or null
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', tripId)
        ?.eq('user_id', user?.id) // Ensure user owns the trip
        ?.select()
        ?.single();

      if (error) throw error;

      // Log the visibility change
      await this.logActivity(
        user?.id,
        tripId,
        'edited',
        `Changed trip visibility override to ${visibilityOverride || 'profile default'}`,
        {
          visibility_override: visibilityOverride,
          trip_destination: data?.destination
        }
      );

      return {
        id: data?.id,
        visibilityOverride: data?.visibility_override,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating trip visibility override:', error);
      throw new Error(error?.message || 'Failed to update trip visibility');
    }
  },

  // Get a single trip by ID with itinerary
  async getTripById(tripId) {
    // ✅ CRITICAL FIX: Add detailed logging for debugging
    console.log('📍 getTripById called with tripId:', tripId);

    const { data: { user } } = await supabase?.auth?.getUser();
    
    // ✅ CRITICAL FIX: Log authentication state
    console.log('👤 Current user:', user?.id);

    // ✅ FIXED: Fetch trip with creator information using correct column name 'full_name'
    const { data: tripData, error: tripError } = await supabase?.from('trips')?.select(`
        *,
        user_profiles!trips_user_id_fkey (
          full_name
        )
      `)?.eq('id', tripId)?.single();

    // ✅ CRITICAL FIX: Log database query result
    console.log('🗄️ Trip query result:', tripData);
    console.log('❌ Trip query error:', tripError);

    if (tripError) {
      console.error('Failed to fetch trip:', tripError);
      throw tripError;
    }
    if (!tripData) {
      console.error('Trip not found:', tripId);
      throw new Error('Trip not found');
    }

    // ✅ NEW: Check if city or state is missing and attempt to populate from Google Places
    if ((!tripData?.city || !tripData?.state) && tripData?.park && user?.id === tripData?.user_id) {
      console.log('🔄 City or state missing, checking cache and Google Places...');
      
      // First check cache
      const cachedLocation = getCachedLocationData(tripData?.park);
      
      if (cachedLocation) {
        console.log('✅ Using cached location data:', cachedLocation);
        
        // Build update object with missing fields from cache
        const updateData = {};
        if (!tripData?.city && cachedLocation?.city) {
          updateData.city = cachedLocation?.city;
          tripData.city = cachedLocation?.city; // Update local data
        }
        if (!tripData?.state && cachedLocation?.state) {
          updateData.state = cachedLocation?.state;
          tripData.state = cachedLocation?.state; // Update local data
        }
        if (!tripData?.place_id && cachedLocation?.placeId) {
          updateData.place_id = cachedLocation?.placeId;
          tripData.place_id = cachedLocation?.placeId; // Update local data
        }
        
        // Update database if we found missing data in cache
        if (Object.keys(updateData)?.length > 0) {
          console.log('💾 Updating trip with cached city/state data:', updateData);
          const { error: updateError } = await supabase
            ?.from('trips')
            ?.update(updateData)
            ?.eq('id', tripId);
            
          if (updateError) {
            console.warn('⚠️ Could not update trip with cached city/state:', updateError);
          } else {
            console.log('✅ Successfully updated trip with cached city/state');
          }
        }
      } else {
        // If not in cache, fetch from Google Places
        try {
          const placeDetails = await searchPlaceWithDetails(
            tripData?.park,
            null // No location bias
          );
          
          if (placeDetails) {
            console.log('✅ Found place details from Google Places:', placeDetails);
            
            // Build update object with missing fields
            const updateData = {};
            if (!tripData?.city && placeDetails?.city) {
              updateData.city = placeDetails?.city;
              tripData.city = placeDetails?.city; // Update local data
            }
            if (!tripData?.state && placeDetails?.state) {
              updateData.state = placeDetails?.state;
              tripData.state = placeDetails?.state; // Update local data
            }
            if (!tripData?.place_id && placeDetails?.placeId) {
              updateData.place_id = placeDetails?.placeId;
              tripData.place_id = placeDetails?.placeId; // Update local data
            }
            
            // Cache the location data for future use
            saveLocationToCache(tripData?.park, {
              state: placeDetails?.state,
              city: placeDetails?.city,
              placeId: placeDetails?.placeId
            });
            
            // Update database if we found missing data
            if (Object.keys(updateData)?.length > 0) {
              console.log('💾 Updating trip with Google Places city/state data:', updateData);
              const { error: updateError } = await supabase
                ?.from('trips')
                ?.update(updateData)
                ?.eq('id', tripId);
                
              if (updateError) {
                console.warn('⚠️ Could not update trip with city/state:', updateError);
                // Continue without updating - we still have the data in memory
              } else {
                console.log('✅ Successfully updated trip with Google Places city/state');
              }
            }
          }
        } catch (placeError) {
          console.warn('⚠️ Could not fetch place details:', placeError);
          // Continue without Google Places data
        }
      }
    }

    // ✅ CRITICAL FIX: Fetch itinerary separately with detailed logging
    console.log('📋 Fetching itinerary for trip:', tripId);
    
    const { data: itineraryData, error: itineraryError } = await supabase?.from('trip_itinerary')?.select('*')?.eq('trip_id', tripId)?.order('day_number', { ascending: true })?.order('start_time', { ascending: true });

    // ✅ CRITICAL FIX: Log itinerary fetch result
    console.log('📋 Itinerary query result:', itineraryData);
    console.log('❌ Itinerary query error:', itineraryError);

    if (itineraryError) {
      console.warn('Could not load itinerary:', itineraryError?.message);
      // ✅ NEW: Continue without itinerary rather than failing
    }

    // ✅ CRITICAL FIX: Map itinerary data with validation
    const mappedItinerary = (itineraryData || [])?.map(item => ({
      id: item?.id,
      tripId: item?.trip_id,
      dayNumber: item?.day_number,
      title: item?.title,
      description: item?.description,
      startTime: item?.start_time,
      endTime: item?.end_time,
      location: item?.location,
      activityType: item?.activity_type,
      cost: item?.cost,
      notes: item?.notes,
      createdAt: item?.created_at,
      updatedAt: item?.updated_at
    }));

    console.log('✅ Mapped itinerary:', mappedItinerary?.length, 'items');

    // ✅ UPDATED: Parse AI trip data if available
    let aiTripData = null;
    if (tripData?.ai_trip_data) {
      try {
        aiTripData = typeof tripData?.ai_trip_data === 'string' ? JSON.parse(tripData?.ai_trip_data) : tripData?.ai_trip_data;
        console.log('🤖 Parsed AI trip data successfully');
      } catch (parseError) {
        console.warn('Could not parse AI trip data:', parseError);
      }
    }

    // ✅ FIXED: Parse full name into first and last for backwards compatibility
    let creatorFirstName = null;
    let creatorLastName = null;
    if (tripData?.user_profiles?.full_name) {
      const nameParts = tripData?.user_profiles?.full_name?.split(' ');
      creatorFirstName = nameParts?.[0] || null;
      creatorLastName = nameParts?.slice(1)?.join(' ') || null;
    }

    // ✅ UPDATED: Convert to camelCase with creator info from full_name, place_id, state, and city
    const result = {
      id: tripData?.id,
      userId: tripData?.user_id,
      title: tripData?.title,
      destination: tripData?.destination,
      park: tripData?.park,
      state: tripData?.state,
      city: tripData?.city,
      placeId: tripData?.place_id,
      startDate: tripData?.start_date,
      endDate: tripData?.end_date,
      duration: tripData?.duration,
      travelers: tripData?.travelers,
      description: tripData?.description,
      summary: tripData?.summary,
      activities: tripData?.activities,
      accommodation: tripData?.accommodation,
      totalCost: tripData?.total_cost,
      notes: tripData?.notes,
      status: tripData?.status,
      isPublic: tripData?.is_public,
      imageUrl: tripData?.image_url,
      imageAlt: tripData?.image_alt,
      createdAt: tripData?.created_at,
      updatedAt: tripData?.updated_at,
      timeframeText: tripData?.timeframe_text,
      visibilityOverride: tripData?.visibility_override,
      aiTripData: aiTripData,
      itinerary: mappedItinerary,
      creatorFirstName: creatorFirstName,
      creatorLastName: creatorLastName
    };

    console.log('🎯 Final trip result:', result);
    return result;
  },

  // ✅ UPDATED: Get public trips for Adventures page using new function
  async getPublicTripsForAdventures(limit = 50) {
    try {
      const { data, error } = await supabase
        ?.rpc('get_public_trips', { p_limit: limit });

      if (error) throw error;

      return data?.map(trip => ({
        id: trip?.id,
        userId: trip?.user_id,
        destination: trip?.destination,
        park: trip?.park,
        description: trip?.description,
        startDate: trip?.start_date,
        endDate: trip?.end_date,
        duration: trip?.duration,
        travelers: trip?.travelers,
        status: trip?.status,
        imageUrl: trip?.image_url,
        imageAlt: trip?.image_alt,
        totalCost: trip?.total_cost,
        activities: trip?.activities || [],
        accommodation: trip?.accommodation,
        notes: trip?.notes,
        title: trip?.title,
        createdAt: trip?.created_at,
        updatedAt: trip?.updated_at,
        effectiveVisibility: trip?.effective_visibility
      })) || [];
    } catch (error) {
      console.error('Error fetching public trips for Adventures:', error);
      return [];
    }
  },

  // Get public trips for showcase
  async getPublicTrips(limit = 6) {
    try {
      const { data, error } = await supabase?.from('trips')?.select(`
          *,
          trip_itinerary (
            id,
            day_number,
            title,
            description,
            start_time,
            end_time,
            location,
            activity_type,
            cost,
            notes
          )
        `)?.eq('is_public', true)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      return data?.map(trip => ({
        id: trip?.id,
        userId: trip?.user_id,
        destination: trip?.destination,
        park: trip?.park,
        description: trip?.description,
        startDate: trip?.start_date,
        endDate: trip?.end_date,
        duration: trip?.duration,
        travelers: trip?.travelers,
        status: trip?.status,
        imageUrl: trip?.image_url,
        imageAlt: trip?.image_alt,
        totalCost: trip?.total_cost,
        activities: trip?.activities || [],
        accommodation: trip?.accommodation,
        notes: trip?.notes,
        isPublic: trip?.is_public,
        createdAt: trip?.created_at,
        updatedAt: trip?.updated_at,
        itinerary: trip?.trip_itinerary?.map(item => ({
          id: item?.id,
          dayNumber: item?.day_number,
          title: item?.title,
          description: item?.description,
          startTime: item?.start_time,
          endTime: item?.end_time,
          location: item?.location,
          activityType: item?.activity_type,
          cost: item?.cost,
          notes: item?.notes
        })) || []
      })) || [];
    } catch (error) {
      console.error('Error fetching public trips:', error);
      return []; // Return empty array instead of throwing
    }
  },

  // Get recent trips for showcase (includes any status, not just completed)
  async getRecentTripsForShowcase(limit = 6) {
    try {
      const { data, error } = await supabase?.from('trips')?.select(`
          *,
          trip_itinerary (
            id,
            day_number,
            title,
            description,
            start_time,
            end_time,
            location,
            activity_type,
            cost,
            notes
          )
        `)?.eq('is_public', true)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      return data?.map(trip => ({
        id: trip?.id,
        userId: trip?.user_id,
        destination: trip?.destination,
        park: trip?.park,
        description: trip?.description,
        startDate: trip?.start_date,
        endDate: trip?.end_date,
        duration: trip?.duration,
        travelers: trip?.travelers,
        status: trip?.status,
        imageUrl: trip?.image_url,
        imageAlt: trip?.image_alt,
        totalCost: trip?.total_cost,
        activities: trip?.activities || [],
        accommodation: trip?.accommodation,
        notes: trip?.notes,
        isPublic: trip?.is_public,
        createdAt: trip?.created_at,
        updatedAt: trip?.updated_at,
        itinerary: trip?.trip_itinerary?.map(item => ({
          id: item?.id,
          dayNumber: item?.day_number,
          title: item?.title,
          description: item?.description,
          startTime: item?.start_time,
          endTime: item?.end_time,
          location: item?.location,
          activityType: item?.activity_type,
          cost: item?.cost,
          notes: item?.notes
        })) || []
      })) || [];
    } catch (error) {
      console.error('Error fetching recent trips for showcase:', error);
      return [];
    }
  },

  // Create new trip
  async createTrip(tripData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // ✅ NEW: Log initial trip data
      console.log('🔵 createTrip called with data:', {
        park: tripData?.park,
        state: tripData?.state,
        city: tripData?.city,
        placeId: tripData?.placeId
      });

      // ✅ Enhance trip data with automatic park information (now includes city)
      const enhancedTripData = await enhanceTripWithParkData(tripData);

      // ✅ NEW: Log enhanced trip data to verify city/state population
      console.log('🟢 Enhanced trip data after enhanceTripWithParkData:', {
        park: enhancedTripData?.park,
        state: enhancedTripData?.state,
        city: enhancedTripData?.city,
        placeId: enhancedTripData?.placeId,
        destination: enhancedTripData?.destination
      });

      // ✅ UPDATED: Apply default Friday-Sunday dates if no dates provided
      const defaultDates = (!enhancedTripData?.startDate && !enhancedTripData?.endDate && !enhancedTripData?.timeframeText) 
        ? getUpcomingWeekendDates() 
        : { startDate: null, endDate: null };

      // ✅ NEW: Get user's default trip privacy preference
      let defaultTripPrivacy = false; // Default to private
      try {
        const { userService } = await import('./userService');
        const userPreferences = await userService?.getUserPreferences(user?.id);
        defaultTripPrivacy = userPreferences?.defaultTripPrivacy === 'public';
      } catch (prefError) {
        console.warn('Could not load user preferences, using default privacy:', prefError);
      }

      const insertData = {
        user_id: user?.id,
        destination: enhancedTripData?.destination,
        park: enhancedTripData?.park,
        state: enhancedTripData?.state,
        city: enhancedTripData?.city,
        place_id: enhancedTripData?.placeId,
        description: enhancedTripData?.description,
        start_date: enhancedTripData?.startDate || defaultDates?.startDate,
        end_date: enhancedTripData?.endDate || defaultDates?.endDate,
        timeframe_text: enhancedTripData?.timeframeText || null,
        duration: enhancedTripData?.duration || null,
        travelers: enhancedTripData?.travelers,
        status: enhancedTripData?.status || 'draft',
        image_url: enhancedTripData?.imageUrl,
        image_alt: enhancedTripData?.imageAlt,
        total_cost: enhancedTripData?.totalCost || 0,
        activities: enhancedTripData?.activities || [],
        accommodation: enhancedTripData?.accommodation,
        notes: enhancedTripData?.notes,
        is_public: enhancedTripData?.isPublic !== undefined ? enhancedTripData?.isPublic : defaultTripPrivacy,
        ai_trip_data: enhancedTripData?.aiTripData
      };

      // ✅ NEW: Critical logging before database insert
      console.log('🔴 CRITICAL: Data being inserted into database:', {
        park: insertData?.park,
        state: insertData?.state,
        city: insertData?.city,
        place_id: insertData?.place_id,
        destination: insertData?.destination
      });

      // ✅ NEW: Validate if Google Maps API key is configured
      const googleMapsApiKey = import.meta?.env?.VITE_GOOGLE_MAPS_API_KEY;
      if (!googleMapsApiKey && !insertData?.city && !insertData?.state) {
        console.warn('⚠️ CRITICAL: Google Maps API key is not configured. City and state cannot be automatically populated.');
        console.warn('⚠️ To fix this: Add VITE_GOOGLE_MAPS_API_KEY to your .env file');
      }

      const { data, error } = await supabase?.from('trips')?.insert(insertData)?.select()?.single();

      if (error) throw error;

      // ✅ NEW: Log what was actually saved to database
      console.log('✅ Trip saved to database:', {
        id: data?.id,
        park: data?.park,
        state: data?.state,
        city: data?.city,
        place_id: data?.place_id,
        destination: data?.destination
      });

      // ✅ Activity logging with date information
      const activityType = insertData?.status === 'draft' ? 'saved_for_later' : 'created';
      const dateInfo = defaultDates?.startDate 
        ? ` (Default dates: ${defaultDates?.startDate} to ${defaultDates?.endDate})` 
        : '';
      
      await this.logActivity(
        user?.id,
        data?.id,
        activityType,
        `${activityType === 'saved_for_later' ? 'Saved trip for later' : 'Created new trip'}: ${data?.destination || 'Untitled'}${dateInfo}`,
        {
          destination: data?.destination,
          park: data?.park,
          status: data?.status,
          travelers: data?.travelers,
          start_date: data?.start_date,
          end_date: data?.end_date,
          used_default_dates: !!defaultDates?.startDate,
          is_public: data?.is_public,
          used_default_privacy: enhancedTripData?.isPublic === undefined
        }
      );

      return {
        id: data?.id,
        userId: data?.user_id,
        destination: data?.destination,
        park: data?.park,
        state: data?.state,
        city: data?.city,
        placeId: data?.place_id,
        description: data?.description,
        startDate: data?.start_date,
        endDate: data?.end_date,
        timeframeText: data?.timeframe_text,
        duration: data?.duration,
        travelers: data?.travelers,
        status: data?.status,
        imageUrl: data?.image_url,
        imageAlt: data?.image_alt,
        totalCost: data?.total_cost,
        activities: data?.activities,
        accommodation: data?.accommodation,
        notes: data?.notes,
        isPublic: data?.is_public,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating trip:', error);
      throw error;
    }
  },

  // Update trip
  async updateTrip(tripId, updates) {
    try {
      // Get current user for activity logging
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Fetch original trip data to detect changes
      const { data: originalTrip } = await supabase
        ?.from('trips')
        ?.select('*')
        ?.eq('id', tripId)
        ?.single();

      // ✅ Build update object with proper null handling
      const updateData = {};
      
      const shouldInclude = (value) => {
        return value !== undefined && value !== "null" && value !== 'null';
      };
      
      // Only include fields that are actually being updated
      if (shouldInclude(updates?.title)) updateData.title = updates?.title;
      if (shouldInclude(updates?.summary)) updateData.summary = updates?.summary; // ✅ NEW: Handle summary updates
      if (shouldInclude(updates?.startDate)) updateData.start_date = updates?.startDate;
      if (shouldInclude(updates?.endDate)) updateData.end_date = updates?.endDate;
      if (shouldInclude(updates?.duration)) updateData.duration = updates?.duration;
      if (shouldInclude(updates?.travelers)) updateData.travelers = updates?.travelers;
      if (shouldInclude(updates?.accommodation)) updateData.accommodation = updates?.accommodation;
      if (shouldInclude(updates?.totalCost)) updateData.total_cost = updates?.totalCost;
      if (shouldInclude(updates?.status)) updateData.status = updates?.status;
      if (shouldInclude(updates?.activities)) updateData.activities = updates?.activities;
      
      updateData.updated_at = new Date()?.toISOString();

      // ✅ NEW: Detect if trip content changed (dates, duration, activities) and regenerate summary
      const contentChanged = (
        (updateData?.start_date && originalTrip?.start_date !== updateData?.start_date) ||
        (updateData?.end_date && originalTrip?.end_date !== updateData?.end_date) ||
        (updateData?.duration && originalTrip?.duration !== updateData?.duration) ||
        (updateData?.activities && JSON.stringify(originalTrip?.activities) !== JSON.stringify(updateData?.activities))
      );

      // If content changed and summary wasn't manually updated, regenerate it
      if (contentChanged && !shouldInclude(updates?.summary)) {
        try {
          // Build trip data for summary generation
          const tripDataForSummary = {
            park: originalTrip?.park,
            destination: originalTrip?.destination,
            duration: updateData?.duration || originalTrip?.duration,
            activities: updateData?.activities || originalTrip?.activities,
            travelers: updateData?.travelers || originalTrip?.travelers,
            accommodation: updateData?.accommodation || originalTrip?.accommodation,
            timeframeText: originalTrip?.timeframe_text
          };

          const newSummary = await tripSummaryService?.generateTripSummary(tripDataForSummary);
          updateData.summary = newSummary;
          console.log('✅ Auto-regenerated trip summary due to content changes');
        } catch (summaryError) {
          console.warn('⚠️ Could not auto-regenerate summary:', summaryError);
          // Continue without updating summary if generation fails
        }
      }

      console.log('✅ Updating trip with sanitized data:', updateData);

      const { data, error } = await supabase
        ?.from('trips')
        ?.update(updateData)
        ?.eq('id', tripId)
        ?.select()
        ?.single();

      if (error) {
        console.error('❌ Supabase update error:', error);
        throw error;
      }

      console.log('✅ Trip updated successfully:', data);

      // ✅ NEW: Track title changes
      if (originalTrip && updates?.title && originalTrip?.title !== updates?.title) {
        await this.logActivity(
          user?.id,
          tripId,
          'edited',
          `Updated trip title from "${originalTrip?.title || 'Untitled'}" to "${updates?.title}"`,
          {
            old_title: originalTrip?.title,
            new_title: updates?.title,
            trip_destination: data?.destination
          }
        );
      }

      // ✅ Track specific types of updates
      if (originalTrip) {
        // Track date modifications
        if (originalTrip?.start_date !== updateData?.start_date || 
            originalTrip?.end_date !== updateData?.end_date) {
          await this.logActivity(
            user?.id,
            tripId,
            'date_modified',
            `Modified trip dates for ${data?.destination || 'trip'}`,
            {
              old_start_date: originalTrip?.start_date,
              new_start_date: updateData?.start_date,
              old_end_date: originalTrip?.end_date,
              new_end_date: updateData?.end_date
            }
          );
        }

        // Track status changes (including confirmation)
        if (originalTrip?.status !== updateData?.status && updateData?.status) {
          const activityType = updateData?.status === 'upcoming' ? 'confirmed' : 'status_changed';
          await this.logActivity(
            user?.id,
            tripId,
            activityType,
            `Changed trip status from ${originalTrip?.status} to ${updateData?.status}`,
            {
              old_status: originalTrip?.status,
              new_status: updateData?.status,
              trip_destination: data?.destination
            }
          );
        }

        // Track reservation updates (travelers/accommodation changes)
        if (originalTrip?.travelers !== updateData?.travelers || 
            originalTrip?.accommodation !== updateData?.accommodation) {
          await this.logActivity(
            user?.id,
            tripId,
            'reservation_updated',
            `Updated reservations for ${data?.destination || 'trip'}`,
            {
              old_travelers: originalTrip?.travelers,
              new_travelers: updateData?.travelers,
              old_accommodation: originalTrip?.accommodation,
              new_accommodation: updateData?.accommodation
            }
          );
        }
      }

      return data;
    } catch (error) {
      console.error('Error updating trip:', error);
      throw new Error(error?.message || 'Failed to update trip');
    }
  },

  // ✅ NEW: Update trip privacy
  async updateTripPrivacy(tripId, isPublic) {
    try {
      // ✅ CRITICAL FIX: Get authenticated user and verify ownership
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('trips')
        ?.update({ 
          is_public: isPublic,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', tripId)
        ?.eq('user_id', user?.id) // ✅ CRITICAL: Ensure user owns the trip
        ?.select()
        ?.single();

      if (error) throw error;

      // ✅ CRITICAL: Check if update was successful (no rows returned = not authorized)
      if (!data) {
        throw new Error('Trip not found or you do not have permission to modify it');
      }

      // ✅ FIXED: Changed from 'privacy_changed' to 'edited' to match existing enum
      await this.logActivity(
        user?.id,
        tripId,
        'edited',
        `Changed trip privacy to ${isPublic ? 'public' : 'private'}`,
        {
          is_public: isPublic,
          trip_destination: data?.destination,
          privacy_change: true // ✅ Added flag to identify privacy-specific edits
        }
      );

      return {
        id: data?.id,
        isPublic: data?.is_public,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating trip privacy:', error);
      throw new Error(error?.message || 'Failed to update trip privacy');
    }
  },

  // ✅ NEW: Add method to update only the summary
  async updateTripSummary(tripId, summary) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const data = await tripSummaryService?.updateTripSummary(tripId, summary);

      // Log the summary update
      await this.logActivity(
        user?.id,
        tripId,
        'edited',
        'Updated trip summary',
        {
          summary: summary?.substring(0, 100) + '...', // Store truncated version in metadata
          trip_destination: data?.destination
        }
      );

      return {
        id: data?.id,
        summary: data?.summary,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating trip summary:', error);
      throw new Error(error?.message || 'Failed to update trip summary');
    }
  },

  // ✅ NEW: Add method to regenerate summary
  async regenerateTripSummary(tripId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Fetch current trip data
      const trip = await this.getTripById(tripId);
      
      // Generate new summary
      const summary = await tripSummaryService?.autoGenerateSummary(tripId, trip);

      // Log the regeneration
      await this.logActivity(
        user?.id,
        tripId,
        'edited',
        'Regenerated trip summary using AI',
        {
          trip_destination: trip?.destination
        }
      );

      return summary;
    } catch (error) {
      console.error('Error regenerating trip summary:', error);
      throw new Error(error?.message || 'Failed to regenerate trip summary');
    }
  },

  // Delete trip
  async deleteTrip(tripId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase?.from('trips')?.delete()?.eq('id', tripId)?.eq('user_id', user?.id);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error deleting trip:', error);
      throw error;
    }
  },

  // Create trip with itinerary in a single transaction
  async createTripWithItinerary(tripData, itineraryItems = [], aiTripData = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // ✅ NEW: Log initial trip data
      console.log('🔵 createTripWithItinerary called with data:', {
        park: tripData?.park,
        state: tripData?.state,
        city: tripData?.city,
        placeId: tripData?.placeId
      });

      // ✅ Enhance trip data with automatic park information (now includes city)
      const enhancedTripData = await enhanceTripWithParkData(tripData);

      // ✅ NEW: Log enhanced trip data to verify city/state population
      console.log('🟢 Enhanced trip data after enhanceTripWithParkData:', {
        park: enhancedTripData?.park,
        state: enhancedTripData?.state,
        city: enhancedTripData?.city,
        placeId: enhancedTripData?.placeId,
        destination: enhancedTripData?.destination
      });

      // ✅ UPDATED: Apply default Friday-Sunday dates if no dates provided
      const defaultDates = (!enhancedTripData?.startDate && !enhancedTripData?.endDate && !enhancedTripData?.timeframeText) 
        ? getUpcomingWeekendDates() 
        : { startDate: null, endDate: null };

      // ✅ NEW: Get user's default trip privacy preference
      let defaultTripPrivacy = false; // Default to private
      try {
        const { userService } = await import('./userService');
        const userPreferences = await userService?.getUserPreferences(user?.id);
        defaultTripPrivacy = userPreferences?.defaultTripPrivacy === 'public';
      } catch (prefError) {
        console.warn('Could not load user preferences, using default privacy:', prefError);
      }

      const insertData = {
        user_id: user?.id,
        destination: enhancedTripData?.destination,
        park: enhancedTripData?.park,
        state: enhancedTripData?.state,
        city: enhancedTripData?.city,
        place_id: enhancedTripData?.placeId,
        description: enhancedTripData?.description,
        start_date: enhancedTripData?.startDate || defaultDates?.startDate,
        end_date: enhancedTripData?.endDate || defaultDates?.endDate,
        timeframe_text: enhancedTripData?.timeframeText || null,
        duration: enhancedTripData?.duration || null,
        travelers: enhancedTripData?.travelers,
        status: enhancedTripData?.status || 'draft',
        image_url: enhancedTripData?.imageUrl,
        image_alt: enhancedTripData?.imageAlt,
        total_cost: enhancedTripData?.totalCost || 0,
        activities: enhancedTripData?.activities || [],
        accommodation: enhancedTripData?.accommodation,
        notes: enhancedTripData?.notes,
        is_public: enhancedTripData?.isPublic !== undefined ? enhancedTripData?.isPublic : defaultTripPrivacy,
        ai_trip_data: aiTripData
      };

      // ✅ NEW: Critical logging before database insert
      console.log('🔴 CRITICAL: Data being inserted into database:', {
        park: insertData?.park,
        state: insertData?.state,
        city: insertData?.city,
        place_id: insertData?.place_id,
        destination: insertData?.destination
      });

      // ✅ NEW: Validate if Google Maps API key is configured
      const googleMapsApiKey = import.meta?.env?.VITE_GOOGLE_MAPS_API_KEY;
      if (!googleMapsApiKey && !insertData?.city && !insertData?.state) {
        console.warn('⚠️ CRITICAL: Google Maps API key is not configured. City and state cannot be automatically populated.');
        console.warn('⚠️ To fix this: Add VITE_GOOGLE_MAPS_API_KEY to your .env file');
      }

      // First, create the trip
      const { data: tripRecord, error: tripError } = await supabase?.from('trips')?.insert(insertData)?.select()?.single();

      if (tripError) throw tripError;

      // ✅ NEW: Log what was actually saved to database
      console.log('✅ Trip saved to database:', {
        id: tripRecord?.id,
        park: tripRecord?.park,
        state: tripRecord?.state,
        city: tripRecord?.city,
        place_id: tripRecord?.place_id,
        destination: tripRecord?.destination
      });

      // ✅ Log trip creation with date information
      const dateInfo = defaultDates?.startDate 
        ? ` (Default dates: ${defaultDates?.startDate} to ${defaultDates?.endDate})` 
        : '';
      
      await this.logActivity(
        user?.id,
        tripRecord?.id,
        'created',
        `Created new trip with itinerary: ${tripRecord?.destination || 'Untitled'}${dateInfo}`,
        {
          destination: tripRecord?.destination,
          park: tripRecord?.park,
          status: tripRecord?.status,
          travelers: tripRecord?.travelers,
          start_date: tripRecord?.start_date,
          end_date: tripRecord?.end_date,
          used_default_dates: !!defaultDates?.startDate,
          itinerary_items: itineraryItems?.length,
          is_public: tripRecord?.is_public,
          used_default_privacy: enhancedTripData?.isPublic === undefined
        }
      );

      // Convert itinerary items to database format with proper time handling
      const dbItineraryItems = itineraryItems?.map(item => ({
        trip_id: tripRecord?.id,
        day_number: item?.dayNumber,
        title: item?.title,
        description: item?.description,
        start_time: this.convertTimeToPostgresFormat(item?.startTime),
        end_time: this.convertTimeToPostgresFormat(item?.endTime),
        location: item?.location,
        activity_type: item?.activityType || 'General',
        cost: item?.cost,
        notes: item?.notes
      }));

      // Create itinerary items if provided
      let itineraryRecords = [];
      if (dbItineraryItems?.length > 0) {
        const { data: itineraryData, error: itineraryError } = await supabase?.from('trip_itinerary')?.insert(dbItineraryItems)?.select();

        if (itineraryError) {
          await supabase?.from('trips')?.delete()?.eq('id', tripRecord?.id);
          throw itineraryError;
        }

        itineraryRecords = itineraryData || [];
      }

      // Return the complete trip object with itinerary
      return {
        id: tripRecord?.id,
        userId: tripRecord?.user_id,
        destination: tripRecord?.destination,
        park: tripRecord?.park,
        state: tripRecord?.state,
        city: tripRecord?.city,
        placeId: tripRecord?.place_id,
        description: tripRecord?.description,
        startDate: tripRecord?.start_date,
        endDate: tripRecord?.end_date,
        timeframeText: tripRecord?.timeframe_text,
        duration: tripRecord?.duration,
        travelers: tripRecord?.travelers,
        status: tripRecord?.status,
        imageUrl: tripRecord?.image_url,
        imageAlt: tripRecord?.image_alt,
        totalCost: tripRecord?.total_cost,
        activities: tripRecord?.activities,
        accommodation: tripRecord?.accommodation,
        notes: tripRecord?.notes,
        isPublic: tripRecord?.is_public,
        createdAt: tripRecord?.created_at,
        updatedAt: tripRecord?.updated_at,
        aiTripData: tripRecord?.ai_trip_data,
        itinerary: itineraryRecords?.map(item => ({
          id: item?.id,
          dayNumber: item?.day_number,
          title: item?.title,
          description: item?.description,
          startTime: item?.start_time,
          endTime: item?.end_time,
          location: item?.location,
          activityType: item?.activity_type,
          cost: item?.cost,
          notes: item?.notes
        })) || []
      };
    } catch (error) {
      console.error('Error creating trip with itinerary:', error);
      throw error;
    }
  },

  // Utility function to convert human-readable time to PostgreSQL time format
  convertTimeToPostgresFormat(timeString) {
    if (!timeString) return null;

    // If already in HH:MM:SS or HH:MM format, return as-is
    if (/^\d{1,2}:\d{2}(:\d{2})?$/?.test(timeString?.trim())) {
      // Add seconds if missing
      const time = timeString?.trim();
      return time?.length === 5 ? `${time}:00` : time;
    }

    // Convert human-readable times to 24-hour format
    const timeMap = {
      // Early morning
      'early morning': '06:00:00',
      'dawn': '06:30:00',
      'sunrise': '07:00:00',
      
      // Morning
      'morning': '09:00:00',
      'mid morning': '10:00:00',
      'late morning': '11:00:00',
      
      // Noon/Afternoon
      'noon': '12:00:00',
      'midday': '12:00:00',
      'lunch': '12:30:00',
      'afternoon': '14:00:00',
      'mid afternoon': '15:00:00',
      'late afternoon': '16:00:00',
      
      // Evening
      'evening': '18:00:00',
      'dinner': '19:00:00',
      'dusk': '19:30:00',
      'sunset': '20:00:00',
      
      // Night
      'night': '21:00:00',
      'late night': '22:00:00'
    };

    const lowerTime = timeString?.toLowerCase()?.trim();
    
    // Check for exact matches first
    if (timeMap?.[lowerTime]) {
      return timeMap?.[lowerTime];
    }

    // Check for partial matches
    for (const [key, value] of Object.entries(timeMap)) {
      if (lowerTime?.includes(key)) {
        return value;
      }
    }

    // Try to extract time from strings like "8:00 AM" or "2:30 PM"
    const timeRegex = /(\d{1,2}):?(\d{2})?\s*(am|pm)/i;
    const match = timeString?.match(timeRegex);
    
    if (match) {
      let hour = parseInt(match?.[1]);
      const minute = match?.[2] ? parseInt(match?.[2]) : 0;
      const ampm = match?.[3]?.toLowerCase();

      // Convert to 24-hour format
      if (ampm === 'pm' && hour !== 12) {
        hour += 12;
      } else if (ampm === 'am' && hour === 12) {
        hour = 0;
      }

      return `${hour?.toString()?.padStart(2, '0')}:${minute?.toString()?.padStart(2, '0')}:00`;
    }

    // Try to extract just hour numbers (like "8" or "14")
    const hourMatch = timeString?.match(/^\d{1,2}$/);
    if (hourMatch) {
      let hour = parseInt(hourMatch?.[0]);
      if (hour >= 0 && hour <= 23) {
        return `${hour?.toString()?.padStart(2, '0')}:00:00`;
      }
    }

    // Default fallback based on common words
    if (lowerTime?.includes('early')) return '07:00:00';
    if (lowerTime?.includes('mid')) return '12:00:00';
    if (lowerTime?.includes('late')) return '17:00:00';

    // If no match found, return null (PostgreSQL will accept null for optional time fields)
    console.warn(`Could not parse time: "${timeString}". Setting to null.`);
    return null;
  },

  // ✅ OPTIMIZED: Get trip statistics with caching
  async getTripStats(userId) {
    const cacheKey = `trip_stats_${userId}`;
    const cacheExpiry = 5 * 60 * 1000; // 5 minutes
    
    try {
      // Check cache first
      const cachedData = this._getCachedData(cacheKey, cacheExpiry);
      if (cachedData) {
        console.log('✅ Using cached trip stats');
        return cachedData;
      }

      const { data, error } = await supabase
        ?.from('trips')
        ?.select('id, destination, park, status, total_cost, travelers, start_date, end_date')
        ?.eq('user_id', userId);

      if (error) throw error;

      const today = new Date();
      today?.setHours(0, 0, 0, 0);

      const fourteenDaysFromNow = new Date(today);
      fourteenDaysFromNow?.setDate(today?.getDate() + 14);
      fourteenDaysFromNow?.setHours(0, 0, 0, 0);

      const upcomingTrips = data?.filter(trip => {
        if (!trip?.start_date) return false;
        const startDate = new Date(trip?.start_date);
        startDate?.setHours(0, 0, 0, 0);
        return startDate >= today && startDate < fourteenDaysFromNow && trip?.status !== 'cancelled';
      }) || [];

      const planningTrips = data?.filter(trip => {
        if (!trip?.start_date) return false;
        const startDate = new Date(trip?.start_date);
        startDate?.setHours(0, 0, 0, 0);
        return startDate >= fourteenDaysFromNow && trip?.status !== 'cancelled';
      }) || [];

      const completedTrips = data?.filter(trip => {
        if (!trip?.start_date) return false;
        const startDate = new Date(trip?.start_date);
        startDate?.setHours(0, 0, 0, 0);
        return startDate < today || trip?.status === 'completed';
      }) || [];

      const stats = {
        totalTrips: data?.length || 0,
        upcomingTrips: upcomingTrips?.length || 0,
        planningTrips: planningTrips?.length || 0,
        completedTrips: completedTrips?.length || 0,
        total: data?.length || 0,
        upcoming: upcomingTrips?.length || 0,
        planning: planningTrips?.length || 0,
        completed: completedTrips?.length || 0,
        draft: data?.filter(trip => trip?.status === 'draft')?.length || 0,
        cancelled: data?.filter(trip => trip?.status === 'cancelled')?.length || 0,
        totalSpent: data?.reduce((sum, trip) => sum + (parseFloat(trip?.total_cost) || 0), 0),
        totalTravelers: data?.reduce((sum, trip) => sum + (parseInt(trip?.travelers) || 0), 0),
        nextTrip: upcomingTrips?.length > 0 ? upcomingTrips?.sort((a, b) => 
          new Date(a?.start_date) - new Date(b?.start_date)
        )?.[0] : null
      };

      // Cache the result
      this._setCachedData(cacheKey, stats);
      
      return stats;
    } catch (error) {
      console.error('Error fetching trip stats:', error);
      return {
        totalTrips: 0,
        upcomingTrips: 0,
        planningTrips: 0,
        completedTrips: 0,
        total: 0,
        upcoming: 0,
        planning: 0,
        completed: 0,
        draft: 0,
        cancelled: 0,
        totalSpent: 0,
        totalTravelers: 0,
        nextTrip: null
      };
    }
  },

  // ✅ NEW: Cache management utilities
  _getCachedData(key, expiryMs) {
    try {
      const cached = sessionStorage.getItem(key);
      if (!cached) return null;
      
      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      
      if (now - timestamp > expiryMs) {
        sessionStorage.removeItem(key);
        return null;
      }
      
      return data;
    } catch (error) {
      console.warn('⚠️ Cache read error:', error);
      return null;
    }
  },

  _setCachedData(key, data) {
    try {
      sessionStorage.setItem(key, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.warn('⚠️ Cache write error:', error);
    }
  },

  // ✅ NEW: Clear all caches
  clearCache() {
    try {
      const keys = Object.keys(sessionStorage);
      keys?.forEach(key => {
        if (key?.startsWith('trip_')) {
          sessionStorage.removeItem(key);
        }
      });
      console.log('✅ Trip cache cleared');
    } catch (error) {
      console.warn('⚠️ Cache clear error:', error);
    }
  },

  // Get user activities (recent actions)
  async getUserActivities(userId, limit = 10) {
    try {
      const { data, error } = await supabase
        ?.from('user_activities')
        ?.select(`
          *,
          trips (
            id,
            destination,
            park
          )
        `)
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;

      return data?.map(activity => ({
        id: activity?.id,
        userId: activity?.user_id,
        tripId: activity?.trip_id,
        activityType: activity?.activity_type,
        description: activity?.description,
        createdAt: activity?.created_at,
        metadata: activity?.metadata || {},
        trip: activity?.trips ? {
          id: activity?.trips?.id,
          destination: activity?.trips?.destination,
          park: activity?.trips?.park
        } : null
      })) || [];
    } catch (error) {
      console.error('Error fetching user activities:', error);
      return [];
    }
  },

  // Log user activity
  async logActivity(userId, tripId, activityType, description, metadata = {}) {
    try {
      // ✅ ADDED: Enhanced logging with console output for debugging
      console.log('🔔 Logging activity:', {
        userId,
        tripId,
        activityType,
        description,
        metadata
      });

      // ✅ CRITICAL FIX: Validate trip exists before logging activity
      if (tripId) {
        const { data: tripExists, error: checkError } = await supabase
          ?.from('trips')
          ?.select('id')
          ?.eq('id', tripId)
          ?.single();

        if (checkError || !tripExists) {
          console.warn('⚠️ Trip does not exist, logging activity without trip_id:', {
            tripId,
            activityType,
            error: checkError?.message
          });
          // Set tripId to null if trip doesn't exist
          tripId = null;
        }
      }

      const { data, error } = await supabase
        ?.from('user_activities')
        ?.insert({
          user_id: userId,
          trip_id: tripId, // ✅ Now safely null if trip doesn't exist
          activity_type: activityType,
          description: description,
          metadata: metadata
        })
        ?.select()
        ?.single();

      if (error) {
        console.error('❌ Activity logging failed:', error);
        // ✅ Don't throw - make activity logging non-blocking
        return null;
      }

      console.log('✅ Activity logged successfully:', data);
      return data;
    } catch (error) {
      console.error('Error logging activity:', error);
      // Don't throw - activity logging should be non-blocking
      return null;
    }
  }
};